import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useNavigate } from "react-router-dom";

const SUPABASE_URL = "https://nbhzbstabtkjrhrwfhoj.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5iaHpic3RhYnRranJocndmaG9qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjM3NjcwMjUsImV4cCI6MjA3OTM0MzAyNX0.iJ4w5kRK-jv0n40HxGStEIgVkj0U5pzqzfEpR0ZPOq4";

const MP_PUBLIC_KEY = import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY || "";

interface CardForm {
  cardNumber: string;
  expMonth: string;
  expYear: string;
  cvv: string;
  cardholderName: string;
  docNumber: string;
}

export function CardPayment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "approved" | "rejected">("idle");
  const [form, setForm] = useState<CardForm>({
    cardNumber: "",
    expMonth: "",
    expYear: "",
    cvv: "",
    cardholderName: "",
    docNumber: "",
  });

  function updateField(field: keyof CardForm, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão expirada. Faça login novamente.");
        navigate("/login");
        return;
      }

      if (!MP_PUBLIC_KEY) {
        toast.error("Chave pública do Mercado Pago não configurada.");
        setLoading(false);
        return;
      }

      // Load MercadoPago SDK dynamically
      const mp = await loadMercadoPago(MP_PUBLIC_KEY);

      // Create card token
      const tokenData = await mp.createCardToken({
        cardNumber: form.cardNumber.replace(/\s/g, ""),
        cardExpirationMonth: form.expMonth,
        cardExpirationYear: form.expYear,
        securityCode: form.cvv,
        cardholderName: form.cardholderName,
        identificationType: "CPF",
        identificationNumber: form.docNumber.replace(/\D/g, ""),
      });

      if (!tokenData?.id) {
        throw new Error("Erro ao tokenizar cartão");
      }

      // Send to backend
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          payment_type: "card",
          card_token: tokenData.id,
          installments: 1,
          payment_method_id: tokenData.payment_method?.id || "visa",
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Erro ao processar pagamento");
      }

      if (data.status === "approved") {
        setStatus("approved");
        toast.success("Pagamento aprovado! 🎉");
        setTimeout(() => navigate("/userhome?payment=success"), 2000);
      } else {
        setStatus("rejected");
        toast.error(
          `Pagamento ${data.status === "rejected" ? "rejeitado" : "pendente"}. ${data.status_detail || "Tente novamente."}`
        );
      }
    } catch (err: any) {
      toast.error(err.message || "Erro ao processar pagamento");
    } finally {
      setLoading(false);
    }
  }

  if (status === "approved") {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <CheckCircle2 className="w-16 h-16 text-primary" />
        <h3 className="text-xl font-bold text-primary">Pagamento aprovado!</h3>
        <p className="text-muted-foreground">Redirecionando...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4 max-w-sm mx-auto">
      <div className="space-y-2">
        <Label htmlFor="cardNumber">Número do cartão</Label>
        <Input
          id="cardNumber"
          placeholder="0000 0000 0000 0000"
          value={form.cardNumber}
          onChange={(e) => updateField("cardNumber", e.target.value)}
          maxLength={19}
          required
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label htmlFor="expMonth">Mês</Label>
          <Input
            id="expMonth"
            placeholder="MM"
            value={form.expMonth}
            onChange={(e) => updateField("expMonth", e.target.value)}
            maxLength={2}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="expYear">Ano</Label>
          <Input
            id="expYear"
            placeholder="AA"
            value={form.expYear}
            onChange={(e) => updateField("expYear", e.target.value)}
            maxLength={4}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cvv">CVV</Label>
          <Input
            id="cvv"
            placeholder="123"
            value={form.cvv}
            onChange={(e) => updateField("cvv", e.target.value)}
            maxLength={4}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cardholderName">Nome no cartão</Label>
        <Input
          id="cardholderName"
          placeholder="NOME COMPLETO"
          value={form.cardholderName}
          onChange={(e) => updateField("cardholderName", e.target.value.toUpperCase())}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="docNumber">CPF</Label>
        <Input
          id="docNumber"
          placeholder="000.000.000-00"
          value={form.docNumber}
          onChange={(e) => updateField("docNumber", e.target.value)}
          maxLength={14}
          required
        />
      </div>

      <Button type="submit" className="w-full" size="lg" disabled={loading}>
        {loading ? (
          <>
            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
            Processando...
          </>
        ) : (
          <>
            <CreditCard className="mr-2 w-4 h-4" />
            Pagar R$ 49,00
          </>
        )}
      </Button>

      {status === "rejected" && (
        <p className="text-sm text-destructive text-center">
          Pagamento rejeitado. Verifique os dados e tente novamente.
        </p>
      )}
    </form>
  );
}

// Dynamic SDK loader
async function loadMercadoPago(publicKey: string): Promise<any> {
  return new Promise((resolve, reject) => {
    if ((window as any).MercadoPago) {
      resolve(new (window as any).MercadoPago(publicKey));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://sdk.mercadopago.com/js/v2";
    script.onload = () => {
      if ((window as any).MercadoPago) {
        resolve(new (window as any).MercadoPago(publicKey));
      } else {
        reject(new Error("MercadoPago SDK failed to load"));
      }
    };
    script.onerror = () => reject(new Error("Failed to load MercadoPago SDK"));
    document.head.appendChild(script);
  });
}
